/**
 * Agent Store — Zustand fuer den globalen Agent-Chat
 *
 * Erweitert um Streaming-States fuer SSE Progress-Tracking
 */

import { create } from 'zustand';

const useAgentStore = create((set, get) => ({
  // State
  messages: [],
  sessionId: null,
  isLoading: false,
  pendingApprovals: [],
  error: null,

  // Streaming State — SSE Progress
  isStreaming: false,
  streamingSteps: [],   // Timeline der Schritte: [{ type, tool, status, timestamp, ... }]
  currentStep: null,    // Aktueller Status-Text fuer Header

  // Context — wird vom aktuellen Modul gesetzt
  moduleContext: {},

  // Actions
  setModuleContext: (context) => set({ moduleContext: context }),

  addMessage: (message) => set((state) => ({
    messages: [...state.messages, message]
  })),

  setMessages: (messages) => set({ messages }),

  setSessionId: (sessionId) => set({ sessionId }),

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  setPendingApprovals: (approvals) => set({ pendingApprovals: approvals }),

  addPendingApprovals: (approvals) => set((state) => ({
    pendingApprovals: [...state.pendingApprovals, ...approvals]
  })),

  removeApproval: (approvalId) => set((state) => ({
    pendingApprovals: state.pendingApprovals.filter(a => a.approvalId !== approvalId)
  })),

  clearChat: () => set({
    messages: [],
    sessionId: null,
    pendingApprovals: [],
    error: null,
    streamingSteps: [],
    currentStep: null,
    isStreaming: false
  }),

  // ── Streaming Actions ──────────────────────────────────────────────────

  startStreaming: () => set({
    isStreaming: true,
    isLoading: true,
    streamingSteps: [],
    currentStep: 'Kontext laden...'
  }),

  stopStreaming: () => set({
    isStreaming: false,
    isLoading: false,
    currentStep: null
  }),

  addStreamStep: (step) => set((state) => ({
    streamingSteps: [...state.streamingSteps, {
      ...step,
      timestamp: Date.now()
    }]
  })),

  updateCurrentStep: (text) => set({ currentStep: text }),

  resetStream: () => set({
    streamingSteps: [],
    currentStep: null,
    isStreaming: false
  }),

  /**
   * SSE-Event verarbeiten — zentraler Handler
   */
  handleStreamEvent: (event) => {
    const { addStreamStep, updateCurrentStep } = get();

    switch (event.type) {
      case 'context_loaded':
        addStreamStep({ type: 'context', status: 'done', label: 'Kontext geladen' });
        updateCurrentStep('Denke nach...');
        break;

      case 'thinking':
        addStreamStep({ type: 'thinking', status: 'active', label: 'Denke nach...', step: event.step });
        updateCurrentStep(event.step === 'initial' ? 'Denke nach...' : 'Naechster Schritt...');
        break;

      case 'tool_loop':
        updateCurrentStep(`Schritt ${event.iteration}: ${event.toolCount} Tool${event.toolCount !== 1 ? 's' : ''}...`);
        break;

      case 'tool_start':
        addStreamStep({
          type: 'tool',
          status: 'active',
          tool: event.tool,
          label: `${event.tool}`,
          iteration: event.iteration
        });
        updateCurrentStep(`${event.tool}...`);
        break;

      case 'tool_result': {
        // Letzten aktiven Tool-Step auf done/error setzen
        set((state) => {
          const steps = [...state.streamingSteps];
          const lastToolIdx = steps.findLastIndex(s => s.type === 'tool' && s.status === 'active');
          if (lastToolIdx >= 0) {
            steps[lastToolIdx] = {
              ...steps[lastToolIdx],
              status: event.success ? 'done' : 'error',
              error: event.error
            };
          }
          return { streamingSteps: steps };
        });
        break;
      }

      case 'approval':
        addStreamStep({
          type: 'approval',
          status: 'waiting',
          tool: event.tool,
          label: `Freigabe: ${event.tool}`,
          approvalId: event.approvalId
        });
        updateCurrentStep('Wartet auf Freigabe...');
        break;

      case 'done':
        // Alle aktiven Steps auf done setzen
        set((state) => ({
          streamingSteps: state.streamingSteps.map(s =>
            s.status === 'active' ? { ...s, status: 'done' } : s
          ),
          currentStep: 'Fertig'
        }));
        break;

      default:
        break;
    }
  },

  // Computed
  hasApprovals: () => get().pendingApprovals.length > 0
}));

export default useAgentStore;
